/**
 * Limpeza das chaves de identidade da sessão.
 *
 * Este módulo não importa nada de propósito: ele é usado pelo `services/api`
 * (interceptor de 401) e por hooks que, por sua vez, importam o `api`. Qualquer
 * import aqui arrisca fechar um ciclo.
 */

const TOKEN_API_KEY = '@CANNECT_TOKEN_API'
const USER_KEY = '@CANNECT_USER'
const SIGNED_USER_KEY = '@CANNECT_SIGNED_USER'

/**
 * Chaves que dizem *quem* está logado — e só elas.
 *
 * Fora daqui, deliberadamente:
 *
 * - **Dados de compra** (`@CANNECT:CHECKOUT_CART`, `@CANNECT:ORDER_CHECKOUT_CART`,
 *   seleções de pagamento, `selectedProducts*`). Esta função roda também quando a
 *   sessão expira sozinha (401), que é involuntário: apagar o carrinho ali faria
 *   o paciente montar o pedido, ser mandado para o login e voltar com o carrinho
 *   vazio. Esses dados continuam sendo limpos onde sempre foram — no logout
 *   explícito e na troca de usuário (`usePrepareCart`).
 *
 * - **`@CANNECT:LAST_USER_ID`**. NÃO REMOVA. É a sentinela que o
 *   `usePrepareCart` usa para detectar que quem está logando agora é outra
 *   pessoa e, aí sim, limpar o carrinho anterior. Sem ela o teste
 *   `storedLastUserId !== null` falha e o próximo usuário do mesmo navegador
 *   herda o carrinho de quem saiu.
 *
 * - **`@CANNECT:ANONYMOUS_CART_SESSION`**. É só o `session_cart_id`: o carrinho
 *   do visitante deslogado vive no servidor e essa chave é o ponteiro para ele.
 *   Apagar equivale a jogar o carrinho fora sem conseguir recuperá-lo.
 *
 * - **`@COOKIES_CONSENT`**, que é registro de consentimento e não sessão.
 */
const IDENTITY_KEYS = [TOKEN_API_KEY, USER_KEY, SIGNED_USER_KEY]

export const clearUserIdentity = (): void => {
  try {
    IDENTITY_KEYS.forEach((key) => localStorage.removeItem(key))
  } catch {
    // localStorage indisponível (modo privado//storage bloqueado): não há
    // sessão para limpar e derrubar o boot por causa disso seria pior.
  }
}

/**
 * Sessão pela metade: o usuário ficou no storage sem nenhum sinal de sessão
 * viva pra restaurar.
 *
 * Acontecia quando a sessão expirava — o interceptor de 401 removia só o token
 * e deixava `@CANNECT_USER` para trás. O app então lia `registration_type` dessa
 * chave órfã e se comportava como logado em parte da tela (mostrando preço de
 * cannabis para quem estava deslogado), enquanto as requisições iam pela rota
 * pública.
 *
 * O sinal de "ainda há sessão pra restaurar" era `@CANNECT_TOKEN_API` (token
 * síncrono no localStorage). Desde a migração pro cookie httpOnly (2FA/SSO,
 * portal-cannect-2fa) ninguém mais escreve essa chave — `useTokenApi.tsx` só
 * dá `removeItem` nela — então checá-la sempre dava "sem token" e toda sessão
 * válida virava "órfã" a cada F5, apagando `@CANNECT_USER` antes do boot
 * restaurar a sessão pelo cookie (caso 1.5, F:qa-2fa-sso-battery). O sinal
 * agora é o cookie de dica de SSO que `useTokenApi.tsx:31-33` também usa pra
 * decidir se tenta restaurar sessão — mesma convenção de nome por ambiente.
 */
export const hasOrphanUserIdentity = (): boolean => {
  try {
    const ssoHintCookieName =
      import.meta.env.VITE_APP_ENV === 'production' ? 'cannect_sso_hint' : 'cannect_sso_hint_dev'
    const hasSsoHint = document.cookie.split('; ').some((cookie) => cookie.startsWith(`${ssoHintCookieName}=`))

    return !hasSsoHint && !!localStorage.getItem(USER_KEY)
  } catch {
    return false
  }
}

/** Roda no boot, antes do primeiro render, para os providers já subirem limpos. */
export const clearOrphanUserIdentity = (): void => {
  if (hasOrphanUserIdentity()) clearUserIdentity()
}
